/**
 * ProductBomSpreadsheetCompareModal.tsx
 *
 * Side-by-side comparison modal for Product BOM Spreadsheet configurations.
 * Compares two configurations (Wood & Hardware diff, 4 summary cards, amber highlights).
 * Reuses the comparison logic/UX from MasterBomComparePage.tsx.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  GitCompare,
  X,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Box,
} from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import type {
  BomConfigRow,
  BomSpreadsheetWoodRow,
  BomSpreadsheetHardwareRow,
  ProductBomSpreadsheetData,
} from '../types/bomSpreadsheetTypes'
import { getProductBomDataApi } from '@/services/boms/productBomSpreadsheetApiClient'

interface WoodDiffRow {
  part_name: string
  status: 'identical' | 'changed' | 'only_in_a' | 'only_in_b'
  diff_fields: string[]
  side_a: BomSpreadsheetWoodRow | null
  side_b: BomSpreadsheetWoodRow | null
}

interface HardwareDiffRow {
  name_raw: string
  status: 'identical' | 'changed' | 'only_in_a' | 'only_in_b'
  diff_fields: string[]
  side_a: BomSpreadsheetHardwareRow | null
  side_b: BomSpreadsheetHardwareRow | null
}

interface CompareSummary {
  identical: number
  changed: number
  only_in_a: number
  only_in_b: number
  total: number
}

interface ProductBomSpreadsheetCompareModalProps {
  isOpen: boolean
  onClose: () => void
  productId: string
  productCode: string
  productName: string
  configs: BomConfigRow[]
  currentConfigId: string
  currentData: ProductBomSpreadsheetData
}

/** Presentation-only: amber cell when this field appears in diff_fields. */
export function fieldDiffClass(diffFields: string[], field: string): string {
  return diffFields.includes(field) ? 'bg-amber-100 text-amber-900 font-semibold' : ''
}

/** Presentation-only: amber when any finished-dim field differs. */
export function dimsDiffClass(diffFields: string[]): string {
  return diffFields.includes('dims_finished_t') ||
    diffFields.includes('dims_finished_w') ||
    diffFields.includes('dims_finished_l')
    ? 'bg-amber-100 text-amber-900 font-semibold'
    : ''
}

export function ProductBomSpreadsheetCompareModal({
  isOpen,
  onClose,
  productId,
  productCode,
  productName,
  configs,
  currentConfigId,
  currentData,
}: ProductBomSpreadsheetCompareModalProps) {
  // Config selection
  const [configIdA, setConfigIdA] = useState<string>(currentConfigId)
  const [configIdB, setConfigIdB] = useState<string>(() => {
    const other = configs.find((c) => c.id !== currentConfigId)
    return other ? other.id : currentConfigId
  })

  // Data cache
  const [dataA, setDataA] = useState<ProductBomSpreadsheetData>(currentData)
  const [dataB, setDataB] = useState<ProductBomSpreadsheetData>(currentData)
  const [loading, setLoading] = useState(false)
  const [onlyDiffs, setOnlyDiffs] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  // Sync data when modal opens or selections change
  useEffect(() => {
    if (!isOpen) return
    let isCancelled = false

    async function loadData() {
      setLoading(true)
      try {
        // Load A
        let sideAData = currentData
        if (configIdA !== currentConfigId) {
          const resA = await getProductBomDataApi(productId, configIdA)
          sideAData = {
            productCode,
            productName,
            configCode: configs.find((c) => c.id === configIdA)?.code || productCode,
            wood: resA.wood,
            hardware: resA.hardware,
          }
        }
        if (!isCancelled) setDataA(sideAData)

        // Load B
        let sideBData = currentData
        if (configIdB !== currentConfigId) {
          const resB = await getProductBomDataApi(productId, configIdB)
          sideBData = {
            productCode,
            productName,
            configCode: configs.find((c) => c.id === configIdB)?.code || productCode,
            wood: resB.wood,
            hardware: resB.hardware,
          }
        }
        if (!isCancelled) setDataB(sideBData)
      } catch (err) {
        console.error('Failed to load comparison data', err)
      } finally {
        if (!isCancelled) setLoading(false)
      }
    }

    void loadData()
    return () => {
      isCancelled = true
    }
  }, [isOpen, configIdA, configIdB, productId, currentConfigId, currentData, productCode, productName, configs])

  // Diff calculation
  const { woodDiff, hardwareDiff, summary } = useMemo(() => {
    if (!dataA || !dataB) {
      const emptySummary: CompareSummary = { identical: 0, changed: 0, only_in_a: 0, only_in_b: 0, total: 0 }
      return {
        woodDiff: [] as WoodDiffRow[],
        hardwareDiff: [] as HardwareDiffRow[],
        summary: { wood: emptySummary, hardware: emptySummary, total: emptySummary },
      }
    }

    const norm = (s?: string | null) => (s || '').trim().toLowerCase()

    // 1. Compare Wood
    const woodQueueA = new Map<string, BomSpreadsheetWoodRow[]>()
    for (const w of dataA.wood) {
      const k = norm(w.part_name)
      if (!woodQueueA.has(k)) woodQueueA.set(k, [])
      woodQueueA.get(k)!.push(w)
    }

    const woodQueueB = new Map<string, BomSpreadsheetWoodRow[]>()
    for (const w of dataB.wood) {
      const k = norm(w.part_name)
      if (!woodQueueB.has(k)) woodQueueB.set(k, [])
      woodQueueB.get(k)!.push(w)
    }

    const allWoodKeys: string[] = []
    for (const w of dataA.wood) {
      const k = norm(w.part_name)
      if (!allWoodKeys.includes(k)) allWoodKeys.push(k)
    }
    for (const w of dataB.wood) {
      const k = norm(w.part_name)
      if (!allWoodKeys.includes(k)) allWoodKeys.push(k)
    }

    const woodDiffList: WoodDiffRow[] = []
    let woodIdentical = 0
    let woodChanged = 0
    let woodOnlyA = 0
    let woodOnlyB = 0

    const woodFields = [
      'wood_type_raw',
      'dims_finished_t',
      'dims_finished_w',
      'dims_finished_l',
      'tenon',
      'qty_per',
      'veneer_spec',
      'notes',
    ] as const

    for (const k of allWoodKeys) {
      const listA = woodQueueA.get(k) || []
      const listB = woodQueueB.get(k) || []
      const maxLen = Math.max(listA.length, listB.length)

      for (let i = 0; i < maxLen; i++) {
        const a = listA[i] || null
        const b = listB[i] || null
        const partName = a?.part_name || b?.part_name || k

        if (a && b) {
          const diffFields: string[] = []
          for (const f of woodFields) {
            const valA = String(a[f] ?? '').trim()
            const valB = String(b[f] ?? '').trim()
            if (valA !== valB) {
              diffFields.push(f)
            }
          }

          if (diffFields.length > 0) {
            woodChanged++
            woodDiffList.push({
              part_name: partName,
              status: 'changed',
              diff_fields: diffFields,
              side_a: a,
              side_b: b,
            })
          } else {
            woodIdentical++
            woodDiffList.push({
              part_name: partName,
              status: 'identical',
              diff_fields: [],
              side_a: a,
              side_b: b,
            })
          }
        } else if (a && !b) {
          woodOnlyA++
          woodDiffList.push({
            part_name: partName,
            status: 'only_in_a',
            diff_fields: [],
            side_a: a,
            side_b: null,
          })
        } else if (!a && b) {
          woodOnlyB++
          woodDiffList.push({
            part_name: partName,
            status: 'only_in_b',
            diff_fields: [],
            side_a: null,
            side_b: b,
          })
        }
      }
    }

    // 2. Compare Hardware
    const hwQueueA = new Map<string, BomSpreadsheetHardwareRow[]>()
    for (const h of dataA.hardware) {
      const k = norm(h.name_raw)
      if (!hwQueueA.has(k)) hwQueueA.set(k, [])
      hwQueueA.get(k)!.push(h)
    }

    const hwQueueB = new Map<string, BomSpreadsheetHardwareRow[]>()
    for (const h of dataB.hardware) {
      const k = norm(h.name_raw)
      if (!hwQueueB.has(k)) hwQueueB.set(k, [])
      hwQueueB.get(k)!.push(h)
    }

    const allHwKeys: string[] = []
    for (const h of dataA.hardware) {
      const k = norm(h.name_raw)
      if (!allHwKeys.includes(k)) allHwKeys.push(k)
    }
    for (const h of dataB.hardware) {
      const k = norm(h.name_raw)
      if (!allHwKeys.includes(k)) allHwKeys.push(k)
    }

    const hwDiffList: HardwareDiffRow[] = []
    let hwIdentical = 0
    let hwChanged = 0
    let hwOnlyA = 0
    let hwOnlyB = 0

    const hwFields = [
      'spec_raw',
      'qty_per',
      'uom_hint',
      'color_hint',
      'item_type',
      'notes',
    ] as const

    for (const k of allHwKeys) {
      const listA = hwQueueA.get(k) || []
      const listB = hwQueueB.get(k) || []
      const maxLen = Math.max(listA.length, listB.length)

      for (let i = 0; i < maxLen; i++) {
        const a = listA[i] || null
        const b = listB[i] || null
        const nameRaw = a?.name_raw || b?.name_raw || k

        if (a && b) {
          const diffFields: string[] = []
          for (const f of hwFields) {
            const valA = String(a[f] ?? '').trim()
            const valB = String(b[f] ?? '').trim()
            if (valA !== valB) {
              diffFields.push(f)
            }
          }

          if (diffFields.length > 0) {
            hwChanged++
            hwDiffList.push({
              name_raw: nameRaw,
              status: 'changed',
              diff_fields: diffFields,
              side_a: a,
              side_b: b,
            })
          } else {
            hwIdentical++
            hwDiffList.push({
              name_raw: nameRaw,
              status: 'identical',
              diff_fields: [],
              side_a: a,
              side_b: b,
            })
          }
        } else if (a && !b) {
          hwOnlyA++
          hwDiffList.push({
            name_raw: nameRaw,
            status: 'only_in_a',
            diff_fields: [],
            side_a: a,
            side_b: null,
          })
        } else if (!a && b) {
          hwOnlyB++
          hwDiffList.push({
            name_raw: nameRaw,
            status: 'only_in_b',
            diff_fields: [],
            side_a: null,
            side_b: b,
          })
        }
      }
    }

    const woodSummary: CompareSummary = {
      identical: woodIdentical,
      changed: woodChanged,
      only_in_a: woodOnlyA,
      only_in_b: woodOnlyB,
      total: woodDiffList.length,
    }

    const hwSummary: CompareSummary = {
      identical: hwIdentical,
      changed: hwChanged,
      only_in_a: hwOnlyA,
      only_in_b: hwOnlyB,
      total: hwDiffList.length,
    }

    const totalSummary: CompareSummary = {
      identical: woodIdentical + hwIdentical,
      changed: woodChanged + hwChanged,
      only_in_a: woodOnlyA + hwOnlyA,
      only_in_b: woodOnlyB + hwOnlyB,
      total: woodDiffList.length + hwDiffList.length,
    }

    return {
      woodDiff: woodDiffList,
      hardwareDiff: hwDiffList,
      summary: { wood: woodSummary, hardware: hwSummary, total: totalSummary },
    }
  }, [dataA, dataB])

  if (!isOpen) return null

  const filteredWood = onlyDiffs ? woodDiff.filter((d) => d.status !== 'identical') : woodDiff
  const filteredHardware = onlyDiffs
    ? hardwareDiff.filter((d) => d.status !== 'identical')
    : hardwareDiff

  const cfgA = configs.find((c) => c.id === configIdA)
  const cfgB = configs.find((c) => c.id === configIdB)

  function formatConfigLabel(c?: BomConfigRow) {
    if (!c) return '—'
    const color = c.colors?.length ? c.colors.join('/') : 'Tất cả màu'
    const asm = c.assemblies?.length ? c.assemblies.join('/') : 'Tất cả kết cấu'
    return `${c.code} · ${color} (${asm})`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs animate-in fade-in">
      <div className="relative flex max-h-[92vh] w-full max-w-[96vw] flex-col rounded-xl bg-white shadow-2xl overflow-hidden border border-slate-300 lg:max-w-7xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-900 px-6 py-4 text-white">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-slate-800 p-2 text-amber-400">
              <GitCompare className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                So Sánh Cấu Hình BOM
                <span className="rounded bg-slate-800 px-2 py-0.5 text-xs font-mono text-slate-300">
                  {productCode}
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Đối chiếu chi tiết từng thông số Gỗ và Ngũ kim giữa hai cấu hình khác nhau
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 1. Selector Bar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
            {/* Side A */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-blue-900 flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[11px] font-black text-white">
                  A
                </span>
                Cấu hình Gốc (Bên A)
              </label>
              <select
                value={configIdA}
                onChange={(e) => setConfigIdA(e.target.value)}
                className="w-full rounded border border-blue-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 shadow-2xs outline-none focus:ring-2 focus:ring-blue-500"
              >
                {configs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {formatConfigLabel(c)}
                  </option>
                ))}
              </select>
            </div>

            {/* Side B */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-emerald-900 flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-black text-white">
                  B
                </span>
                Cấu hình So Sánh (Bên B)
              </label>
              <select
                value={configIdB}
                onChange={(e) => setConfigIdB(e.target.value)}
                className="w-full rounded border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900 shadow-2xs outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {configs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {formatConfigLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 2. 4 Summary Cards & Filter Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700">TỔNG HỢP KHÁC BIỆT</span>
              <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyDiffs}
                  onChange={(e) => setOnlyDiffs(e.target.checked)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                <span>Chỉ hiện dòng có khác biệt</span>
              </label>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {/* Identical */}
              <div className="p-3 rounded-lg bg-white border border-slate-300 shadow-2xs">
                <div className="text-xs font-medium text-slate-700 flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  <span>Giống nhau</span>
                </div>
                <div className="text-xl font-black text-slate-900 mt-0.5">
                  {summary.total.identical}
                </div>
                <Badge tone="success" className="mt-1">
                  Gỗ: {summary.wood.identical} · NK: {summary.hardware.identical}
                </Badge>
              </div>

              {/* Changed */}
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 shadow-2xs">
                <div className="text-xs font-bold text-amber-800 flex items-center justify-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  <span>Khác thông số</span>
                </div>
                <div className="text-xl font-black text-amber-900 mt-0.5">
                  {summary.total.changed}
                </div>
                <div className="text-xs font-semibold text-amber-700 mt-1">
                  Gỗ: {summary.wood.changed} · NK: {summary.hardware.changed}
                </div>
              </div>

              {/* Only in A */}
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 shadow-2xs">
                <div className="text-xs font-medium text-blue-800">Chỉ có ở A</div>
                <div className="text-xl font-black text-blue-900 mt-0.5">
                  {summary.total.only_in_a}
                </div>
                <Badge tone="info" className="mt-1">
                  Gỗ: {summary.wood.only_in_a} · NK: {summary.hardware.only_in_a}
                </Badge>
              </div>

              {/* Only in B */}
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 shadow-2xs">
                <div className="text-xs font-medium text-emerald-800">Chỉ có ở B</div>
                <div className="text-xl font-black text-emerald-900 mt-0.5">
                  {summary.total.only_in_b}
                </div>
                <Badge tone="neutral" className="mt-1">
                  Gỗ: {summary.wood.only_in_b} · NK: {summary.hardware.only_in_b}
                </Badge>
              </div>
            </div>
          </div>

          {loading && (
            <div className="py-6 text-center text-xs text-slate-500">
              Đang tải dữ liệu và so sánh giữa hai cấu hình...
            </div>
          )}

          {/* 3. Wood Diff Table */}
          {!loading && (
            <div className="rounded-lg border border-slate-300 bg-white overflow-hidden shadow-2xs">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-amber-600" />
                  <span className="text-xs font-bold text-slate-900 uppercase">
                    Chi tiết Gỗ (Wood Lines)
                  </span>
                  <Badge tone="neutral">{filteredWood.length} chi tiết</Badge>
                </div>
                <span className="text-[11px] text-amber-700 font-medium">
                  * Ô nền vàng biểu thị giá trị khác nhau giữa hai bên
                </span>
              </div>

              {filteredWood.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400">
                  {onlyDiffs ? 'Không có sự khác biệt nào ở chi tiết gỗ.' : 'Chưa có dữ liệu gỗ.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1100px] text-xs text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-300">
                        <th className="p-2 border-r border-slate-200 text-center w-10">STT</th>
                        <th className="p-2 border-r border-slate-200 text-center w-24">Trạng thái</th>
                        <th className="p-2 border-r border-slate-300 min-w-32">Tên chi tiết</th>
                        {/* Pair: Loại gỗ A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center whitespace-nowrap">
                          Loại gỗ A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center whitespace-nowrap">
                          Loại gỗ B
                        </th>
                        {/* Pair: Quy cách A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center whitespace-nowrap">
                          Quy cách (T×W×L) A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center whitespace-nowrap">
                          Quy cách (T×W×L) B
                        </th>
                        {/* Pair: SL A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center">
                          SL A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center">
                          SL B
                        </th>
                        {/* Pair: Mộng A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center">
                          Mộng A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center">
                          Mộng B
                        </th>
                        {/* Pair: Veneer A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center">
                          Veneer A
                        </th>
                        <th className="p-2 bg-emerald-50 text-emerald-900 text-center">Veneer B</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-normal">
                      {filteredWood.map((item, idx) => {
                        const fmtDims = (row: BomSpreadsheetWoodRow | null) =>
                          row
                            ? `${row.dims_finished_t || 0} × ${row.dims_finished_w || 0} × ${
                                row.dims_finished_l || 0
                              }`
                            : '—'
                        const hiDims = dimsDiffClass(item.diff_fields)

                        return (
                          <tr
                            key={idx}
                            className={`hover:bg-slate-50 ${
                              item.status === 'changed' ? 'bg-amber-50/30' : ''
                            }`}
                          >
                            <td className="p-2 text-center text-slate-500 border-r border-slate-200">
                              {idx + 1}
                            </td>
                            <td className="p-2 text-center border-r border-slate-200">
                              {item.status === 'identical' && (
                                <Badge tone="success">Giống nhau</Badge>
                              )}
                              {item.status === 'changed' && (
                                <Badge tone="warning">Khác biệt</Badge>
                              )}
                              {item.status === 'only_in_a' && (
                                <Badge tone="info">Chỉ có ở A</Badge>
                              )}
                              {item.status === 'only_in_b' && (
                                <Badge tone="neutral">Chỉ có ở B</Badge>
                              )}
                            </td>
                            <td className="p-2 font-bold text-slate-900 border-r border-slate-300">
                              {item.part_name}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'wood_type_raw',
                              )}`}
                            >
                              {item.side_a?.wood_type_raw || '—'}
                            </td>
                            <td
                              className={`p-2 text-center border-r border-slate-300 ${fieldDiffClass(
                                item.diff_fields,
                                'wood_type_raw',
                              )}`}
                            >
                              {item.side_b?.wood_type_raw || '—'}
                            </td>

                            <td
                              className={`p-2 text-center font-mono border-r border-slate-100 ${hiDims}`}
                            >
                              {fmtDims(item.side_a)}
                            </td>
                            <td
                              className={`p-2 text-center font-mono border-r border-slate-300 ${hiDims}`}
                            >
                              {fmtDims(item.side_b)}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'qty_per',
                              )}`}
                            >
                              {item.side_a ? String(item.side_a.qty_per || 0) : '—'}
                            </td>
                            <td
                              className={`p-2 text-center border-r border-slate-300 ${fieldDiffClass(
                                item.diff_fields,
                                'qty_per',
                              )}`}
                            >
                              {item.side_b ? String(item.side_b.qty_per || 0) : '—'}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'tenon',
                              )}`}
                            >
                              {item.side_a?.tenon || '—'}
                            </td>
                            <td
                              className={`p-2 text-center border-r border-slate-300 ${fieldDiffClass(
                                item.diff_fields,
                                'tenon',
                              )}`}
                            >
                              {item.side_b?.tenon || '—'}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'veneer_spec',
                              )}`}
                            >
                              {item.side_a?.veneer_spec || '—'}
                            </td>
                            <td
                              className={`p-2 text-center ${fieldDiffClass(
                                item.diff_fields,
                                'veneer_spec',
                              )}`}
                            >
                              {item.side_b?.veneer_spec || '—'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* 4. Hardware Diff Table */}
          {!loading && (
            <div className="rounded-lg border border-slate-300 bg-white overflow-hidden shadow-2xs">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Box className="h-4 w-4 text-blue-600" />
                  <span className="text-xs font-bold text-slate-900 uppercase">
                    Chi tiết Ngũ Kim (Hardware Lines)
                  </span>
                  <Badge tone="neutral">{filteredHardware.length} vật tư</Badge>
                </div>
                <span className="text-[11px] text-amber-700 font-medium">
                  * Ô nền vàng biểu thị giá trị khác nhau giữa hai bên
                </span>
              </div>

              {filteredHardware.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400">
                  {onlyDiffs ? 'Không có sự khác biệt nào ở ngũ kim.' : 'Chưa có dữ liệu ngũ kim.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-xs text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-300">
                        <th className="p-2 border-r border-slate-200 text-center w-10">STT</th>
                        <th className="p-2 border-r border-slate-200 text-center w-24">Trạng thái</th>
                        <th className="p-2 border-r border-slate-300 min-w-32">Tên vật tư</th>
                        {/* Pair: Quy cách A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center whitespace-nowrap">
                          Quy cách A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center whitespace-nowrap">
                          Quy cách B
                        </th>
                        {/* Pair: SL A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center">
                          SL A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center">
                          SL B
                        </th>
                        {/* Pair: ĐVT A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center">
                          ĐVT A
                        </th>
                        <th className="p-2 border-r border-slate-300 bg-emerald-50 text-emerald-900 text-center">
                          ĐVT B
                        </th>
                        {/* Pair: Màu A|B */}
                        <th className="p-2 border-r border-slate-100 bg-blue-50 text-blue-900 text-center">
                          Màu A
                        </th>
                        <th className="p-2 bg-emerald-50 text-emerald-900 text-center">Màu B</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-normal">
                      {filteredHardware.map((item, idx) => {
                        return (
                          <tr
                            key={idx}
                            className={`hover:bg-slate-50 ${
                              item.status === 'changed' ? 'bg-amber-50/30' : ''
                            }`}
                          >
                            <td className="p-2 text-center text-slate-500 border-r border-slate-200">
                              {idx + 1}
                            </td>
                            <td className="p-2 text-center border-r border-slate-200">
                              {item.status === 'identical' && (
                                <Badge tone="success">Giống nhau</Badge>
                              )}
                              {item.status === 'changed' && (
                                <Badge tone="warning">Khác biệt</Badge>
                              )}
                              {item.status === 'only_in_a' && (
                                <Badge tone="info">Chỉ có ở A</Badge>
                              )}
                              {item.status === 'only_in_b' && (
                                <Badge tone="neutral">Chỉ có ở B</Badge>
                              )}
                            </td>
                            <td className="p-2 font-bold text-slate-900 border-r border-slate-300">
                              {item.name_raw}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'spec_raw',
                              )}`}
                            >
                              {item.side_a?.spec_raw || '—'}
                            </td>
                            <td
                              className={`p-2 text-center border-r border-slate-300 ${fieldDiffClass(
                                item.diff_fields,
                                'spec_raw',
                              )}`}
                            >
                              {item.side_b?.spec_raw || '—'}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'qty_per',
                              )}`}
                            >
                              {item.side_a ? String(item.side_a.qty_per || 0) : '—'}
                            </td>
                            <td
                              className={`p-2 text-center border-r border-slate-300 ${fieldDiffClass(
                                item.diff_fields,
                                'qty_per',
                              )}`}
                            >
                              {item.side_b ? String(item.side_b.qty_per || 0) : '—'}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'uom_hint',
                              )}`}
                            >
                              {item.side_a?.uom_hint || '—'}
                            </td>
                            <td
                              className={`p-2 text-center border-r border-slate-300 ${fieldDiffClass(
                                item.diff_fields,
                                'uom_hint',
                              )}`}
                            >
                              {item.side_b?.uom_hint || '—'}
                            </td>

                            <td
                              className={`p-2 text-center border-r border-slate-100 ${fieldDiffClass(
                                item.diff_fields,
                                'color_hint',
                              )}`}
                            >
                              {item.side_a?.color_hint || '—'}
                            </td>
                            <td
                              className={`p-2 text-center ${fieldDiffClass(
                                item.diff_fields,
                                'color_hint',
                              )}`}
                            >
                              {item.side_b?.color_hint || '—'}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-3">
          <div className="text-xs text-slate-500">
            {configs.length <= 1
              ? 'Sản phẩm chỉ có 1 cấu hình. Hãy thêm cấu hình khác tại trang Mã hàng để so sánh.'
              : `Đang so sánh: ${formatConfigLabel(cfgA)} ↔ ${formatConfigLabel(cfgB)}`}
          </div>
          <Button variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
